import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping } from '@fortawesome/free-solid-svg-icons';
import { commentsService } from '../../services/api';
import type { Comment, User } from '../../types';
import SiteBrandedLoader from '../SiteBrandedLoader';

const toFaDigits = (value: string | number) =>
  String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const formatRelativeDate = (iso: string) => {
  const diff = Date.now() - +new Date(iso);
  const day = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (day <= 0) return 'امروز';
  if (day === 1) return 'دیروز';
  return `${toFaDigits(day)} روز پیش`;
};

function displayBody(c: Comment): string {
  const t = (c.editedContent ?? c.content ?? '').trim();
  return t;
}

type Props = {
  courseId: string;
  allowComments?: boolean;
  isEnrolled: boolean;
  user: User | null;
  onEnroll: () => void;
};

export const CourseTemplateComments: React.FC<Props> = ({
  courseId,
  allowComments = true,
  isEnrolled,
  user,
  onEnroll,
}) => {
  const [items, setItems] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentQuery, setCommentQuery] = useState('');
  const [commentSort, setCommentSort] = useState<'newest' | 'topRated' | 'mostHelpful'>('newest');
  const [commentRateFilter, setCommentRateFilter] = useState<number | 'all'>('all');
  const [commentWithReplyOnly, setCommentWithReplyOnly] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [newCommentRating, setNewCommentRating] = useState(5);
  const [authorPhone, setAuthorPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const defaultAuthorName = useMemo(() => {
    const n = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    if (n.length >= 2) return n;
    return user?.username?.trim() || '';
  }, [user]);

  const [authorName, setAuthorName] = useState(defaultAuthorName);

  useEffect(() => {
    setAuthorName(defaultAuthorName);
  }, [defaultAuthorName]);

  const load = useCallback(async () => {
    if (!courseId || !allowComments) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const data = await commentsService.getCourseComments(courseId);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'خطا در دریافت نظرات');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, allowComments]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    return items.map((c) => {
      const reps = [...(c.replies ?? [])].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
      const ratingStar = typeof c.rating === 'number' && c.rating >= 1 && c.rating <= 5 ? c.rating : 5;
      return {
        id: c.id,
        author: c.authorName,
        roleLabel: 'شرکت‌کننده دوره',
        content: displayBody(c),
        rating: ratingStar,
        createdAt: c.createdAt,
        replies: reps.map((r) => ({
          id: r.id,
          author: r.authorName,
          content: displayBody(r),
          createdAt: r.createdAt,
        })),
      };
    });
  }, [items]);

  const averageRating = useMemo(() => {
    if (!rows.length) return 0;
    const sum = rows.reduce((acc, r) => acc + r.rating, 0);
    return sum / rows.length;
  }, [rows]);

  const filtered = useMemo(() => {
    let list = [...rows];

    if (commentRateFilter !== 'all') {
      list = list.filter((item) => item.rating === commentRateFilter);
    }

    if (commentQuery.trim()) {
      const q = commentQuery.trim().toLowerCase();
      list = list.filter(
        (item) =>
          item.author.toLowerCase().includes(q) ||
          item.content.toLowerCase().includes(q) ||
          item.replies.some((rep) => rep.content.toLowerCase().includes(q) || rep.author.toLowerCase().includes(q)),
      );
    }

    if (commentWithReplyOnly) {
      list = list.filter((item) => item.replies.length > 0);
    }

    if (commentSort === 'topRated') {
      list.sort((a, b) => b.rating - a.rating);
    } else if (commentSort === 'mostHelpful') {
      list.sort((a, b) => {
        const dr = b.replies.length - a.replies.length;
        if (dr !== 0) return dr;
        return b.rating - a.rating;
      });
    } else {
      list.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
    }

    return list;
  }, [rows, commentQuery, commentRateFilter, commentSort, commentWithReplyOnly]);

  const activeCommentFiltersCount =
    (commentRateFilter !== 'all' ? 1 : 0) + (commentWithReplyOnly ? 1 : 0);

  const onSubmitComment = async () => {
    if (!allowComments || !courseId || !newCommentText.trim()) return;
    const name = authorName.trim();
    if (name.length < 2) {
      window.alert('لطفاً نام خود را به‌درستی وارد کنید (حداقل ۲ کاراکتر).');
      return;
    }
    try {
      setSubmitting(true);
      await commentsService.createCourseComment(courseId, {
        authorName: name,
        authorPhone: authorPhone.trim() || undefined,
        content: newCommentText.trim(),
        rating: newCommentRating,
      });
      window.alert('نظر شما ثبت شد. پس از تأیید در پنل ادمین در این صفحه نمایش داده می‌شود.');
      setNewCommentText('');
      setAuthorPhone('');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      window.alert(err?.response?.data?.message || 'خطا در ثبت نظر');
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowComments) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/90 p-6 text-center text-sm text-amber-900">
        ثبت نظر برای این دوره توسط مدیر غیرفعال شده است.
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:gap-4 lg:grid-cols-[300px_1fr]">
      <div className="lg:sticky lg:top-36 lg:z-30 lg:self-start">
        <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:rounded-2xl sm:p-4">
          <h3 className="mb-2 text-sm font-bold text-slate-900 sm:mb-3 sm:text-base">خلاصه و فیلتر</h3>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center sm:rounded-xl sm:p-4">
            <p className="text-2xl font-black text-[#1C3B9B] sm:text-3xl">
              {rows.length ? toFaDigits(averageRating.toFixed(1)) : '—'}
            </p>
            <p className="mt-1 text-xs text-[#64748B]">میانگین امتیاز از ۵</p>
            {rows.length > 0 && (
              <div className="mt-1.5 text-base text-amber-500 sm:mt-2 sm:text-lg">
                {'★'.repeat(Math.round(averageRating))}
              </div>
            )}
            <p className="mt-1 text-xs text-[#64748B]">{toFaDigits(rows.length)} نظر منتشرشده</p>
          </div>
          <div className="mt-3 space-y-3 border-t border-slate-100 pt-3 sm:mt-4 sm:space-y-4 sm:pt-4">
            <div>
              <p className="mb-2 text-[11px] font-bold text-slate-500">امتیاز</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setCommentRateFilter('all')}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition sm:px-3 sm:py-1.5 sm:text-xs ${
                    commentRateFilter === 'all'
                      ? 'bg-[#1C3B9B] text-white shadow-[0_6px_14px_rgba(28,59,155,0.24)]'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  همه
                </button>
                {[5, 4, 3].map((score) => (
                  <button
                    type="button"
                    key={score}
                    onClick={() => setCommentRateFilter(commentRateFilter === score ? 'all' : score)}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition sm:px-3 sm:py-1.5 sm:text-xs ${
                      commentRateFilter === score
                        ? 'bg-[#1C3B9B] text-white shadow-[0_6px_14px_rgba(28,59,155,0.24)]'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {score}★
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] font-semibold text-slate-700 sm:rounded-xl sm:px-2.5 sm:py-2 sm:text-xs">
                <input
                  type="checkbox"
                  checked={commentWithReplyOnly}
                  onChange={(e) => setCommentWithReplyOnly(e.target.checked)}
                  className="rounded border-slate-300"
                />
                فقط با پاسخ مدرس
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-3 sm:rounded-2xl sm:p-4">
          <h3 className="mb-2 text-sm font-bold text-[#0F172A] sm:mb-3">ثبت نظر</h3>
          {!isEnrolled ? (
            <div className="rounded-lg border border-slate-200 bg-white/90 p-4 text-center sm:rounded-xl">
              <p className="text-sm font-bold text-slate-800">فقط برای خریداران دوره</p>
              <p className="mt-1 text-xs leading-6 text-slate-600">
                پس از ثبت‌نام می‌توانید امتیاز و تجربه خود را ثبت کنید. نظر پس از بررسی در پنل ادمین منتشر می‌شود.
              </p>
              <button
                type="button"
                onClick={onEnroll}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#1C3B9B]/20 bg-[#1C3B9B] px-4 py-2 text-xs font-extrabold text-white transition hover:bg-[#16307F]"
              >
                <FontAwesomeIcon icon={faCartShopping} />
                خرید دوره و ثبت نظر
              </button>
            </div>
          ) : (
            <>
              <div className="mb-2 grid gap-2 sm:grid-cols-2 sm:gap-3">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-600">نام نمایشی</label>
                  <input
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-xs focus:border-[#1C3B9B] focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/25 sm:rounded-xl sm:text-sm"
                    maxLength={80}
                    placeholder="نام و نام خانوادگی"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-600">شماره تماس (اختیاری)</label>
                  <input
                    value={authorPhone}
                    onChange={(e) => setAuthorPhone(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-xs focus:border-[#1C3B9B] focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/25 sm:rounded-xl sm:text-sm"
                    maxLength={30}
                    placeholder="۰۹۱۲…"
                  />
                </div>
              </div>
              <div className="mb-2 flex flex-wrap items-center gap-1.5 sm:mb-3 sm:gap-2">
                <span className="text-xs text-[#475569] sm:text-sm">امتیاز شما:</span>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    onClick={() => setNewCommentRating(star)}
                    className={`text-lg sm:text-xl ${star <= newCommentRating ? 'text-amber-500' : 'text-slate-300'}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                rows={3}
                maxLength={5000}
                className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:p-3 sm:text-sm"
                placeholder="تجربه‌ات از این دوره را بنویس..."
              />
              <div className="mt-2 flex justify-end sm:mt-3">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void onSubmitComment()}
                  className="rounded-md bg-gradient-to-r from-[#1C3B9B] to-[#C11277] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 sm:rounded-lg sm:px-4 sm:py-2 sm:text-sm"
                >
                  {submitting ? 'در حال ارسال...' : 'ثبت نظر'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-slate-400">⌕</span>
              <input
                value={commentQuery}
                onChange={(e) => setCommentQuery(e.target.value)}
                placeholder="جستجو در نظرات..."
                className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pr-9 pl-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:py-2 sm:pr-10 sm:pl-3 sm:text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={commentSort}
                onChange={(e) => setCommentSort(e.target.value as 'newest' | 'topRated' | 'mostHelpful')}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/30 focus:border-[#1C3B9B] sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="newest">جدیدترین</option>
                <option value="topRated">بیشترین امتیاز</option>
                <option value="mostHelpful">بیشترین تعامل (پاسخ مدرس)</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  setCommentRateFilter('all');
                  setCommentWithReplyOnly(false);
                  setCommentQuery('');
                  setCommentSort('newest');
                }}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold hover:border-[#1C3B9B] hover:bg-[#F8FAFF] sm:rounded-xl sm:px-3 sm:py-2 sm:text-xs"
              >
                پاکسازی فیلتر
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-[#64748B] sm:gap-2 sm:text-xs">
            <span className="rounded-md bg-slate-100 px-2 py-0.5 font-semibold sm:px-2.5 sm:py-1">
              {loading ? '…' : toFaDigits(filtered.length)} نتیجه
            </span>
            {activeCommentFiltersCount > 0 && (
              <span className="rounded-md bg-[#EAF0FF] px-2 py-0.5 font-semibold text-[#1C3B9B] sm:px-2.5 sm:py-1">
                {toFaDigits(activeCommentFiltersCount)} فیلتر فعال
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="py-10">
              <SiteBrandedLoader
                variant="section"
                size="sm"
                message="در حال بارگذاری نظرات..."
                className="min-h-0 bg-transparent py-0"
              />
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
          ) : (
            filtered.map((comment) => (
              <div
                key={comment.id}
                className="rounded-lg border border-[#DDE5FF] bg-white p-3 shadow-[0_4px_16px_rgba(15,23,42,0.04)] sm:rounded-xl sm:p-4"
              >
                <div className="mb-2 flex items-start justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EAF0FF] text-xs font-bold text-[#1C3B9B] sm:h-10 sm:w-10">
                      {comment.author.slice(0, 1)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A] sm:text-base">{comment.author}</p>
                      <p className="text-[10px] text-[#64748B] sm:text-xs">
                        {comment.roleLabel} • {formatRelativeDate(comment.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-amber-500 sm:text-sm">{'★'.repeat(comment.rating)}</div>
                </div>
                <p className="text-sm leading-6 text-[#475569] sm:leading-7">{comment.content}</p>

                {comment.replies.map((rep) => (
                  <div
                    key={rep.id}
                    className="mt-2 rounded-lg border border-[#CFE0FF] bg-[#F6F9FF] p-2.5 sm:mt-3 sm:rounded-xl sm:p-3"
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#BFD4FF] bg-white text-xs font-bold text-[#1C3B9B] sm:h-12 sm:w-12 sm:rounded-lg sm:text-sm">
                        {rep.author.slice(0, 1)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-1.5 sm:gap-2">
                          <span className="rounded-md bg-[#1C3B9B] px-1.5 py-0.5 text-[10px] font-bold text-white sm:text-xs">
                            پاسخ مدرس
                          </span>
                          <p className="text-[10px] font-bold text-[#1C3B9B] sm:text-xs">{rep.author}</p>
                          <span className="text-[10px] text-slate-500 sm:text-xs">
                            • {formatRelativeDate(rep.createdAt)}
                          </span>
                        </div>
                        <p className="text-xs leading-6 text-[#334155] sm:text-sm">{rep.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-[#64748B]">
              نظری با این فیلتر پیدا نشد یا هنوز نظری منتشر نشده است.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
